import {
  type KeyboardEvent,
  type PointerEvent,
  useCallback,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

import { cn } from '@shared/lib/cn';
import { formatNumber } from '@shared/lib/format';

import {
  CHART_DEFAULT_WIDTH,
  CHART_SIZE_CLASS,
  SERIES_COLOR_VAR,
  SERIES_FILL_CLASS,
  SERIES_STROKE_CLASS,
  TIME_SERIES_LAYOUT as L,
} from './chart.constant';
import { crisp, estimateTextWidth, type LinearScale, niceScale, scaleY } from './chart.lib';
import { CHART_TEXT } from './chart.text';
import { useElementWidth } from './chart-size.hook';
import { useChartTooltip } from './chart-tooltip.hook';
import { ChartTooltip } from './chart-tooltip.ui';
import type { DatavizSeries } from './model/dataviz.types';
import type { TimeSeriesChartProps } from './model/time-series.types';
import { barDrawOrder, labelStride, lineSegments, timeSeriesModel } from './time-series.lib';

const HEIGHT = L.padTop + L.plotHeight + L.xAxisHeight;
const PLOT_TOP = L.padTop;
const PLOT_BOTTOM = L.padTop + L.plotHeight;
/** 그릴 값이 없을 때(덮개 · 자료 없음) 격자선만 그리는 축 */
const EMPTY_SCALE = niceScale(0, 10);

const defaultFormat = (value: number) => formatNumber(value, { maximumFractionDigits: 2 });
const seriesOf = (index: number) => (index + 1) as DatavizSeries;
const PERIOD_KEY = 'period-';

/** 세로축 눈금 글자 칸(단위 글자 · 오른쪽 축) */
function AxisTicks({
  scale,
  side,
  width,
  format,
}: {
  scale: LinearScale;
  side: 'left' | 'right';
  width: number;
  format: (value: number) => string;
}) {
  const y = scaleY(scale, PLOT_TOP, L.plotHeight);
  return scale.ticks.map((tick) => (
    <text
      key={tick}
      x={side === 'left' ? width - L.tickGap : L.tickGap}
      y={y(tick)}
      dominantBaseline="central"
      textAnchor={side === 'left' ? 'end' : 'start'}
      className="fill-dataviz-axis typo-caption"
    >
      {format(tick)}
    </text>
  ));
}

/**
 * 분기 · 연도별 수치 그래프(선 또는 막대, 계열 1~2개). 직접 그린 SVG(DD-54 #1) — 규격 `_chart-spec.md` §2 · §3.
 * - 높이 352 = 위 8 + 그래프 영역 320 + 가로축 글자 24, 너비는 놓이는 자리를 채운다(재어서 다시 그린다 — 1px 선 · 글자가 늘지 않게).
 * - 가로 격자선만 · 0 의 바닥선 · 세로축 눈금(왼쪽, dual-axis 면 오른쪽도) · 단위 글자 "( 단위 )" 를 세로로.
 * - 선: 1px 꺾은선 + 점(지름 6) + 아래 면(계열 색 → 투명 그라디언트, 60%). 막대: 폭 10, 계열 둘이면 같은 자리에 겹친다(큰 값을 뒤에).
 * - null 은 빈 기간(선이 끊기고 막대 · 점이 없다). `breaks` 는 그 기간 앞에서 선을 끊고 세로 구분선(`dataviz/divider`)을 긋는다.
 * - 올리면 그 기간 전체(index)의 값을 ChartTooltip 으로. 화살표 키로도 기간을 옮긴다.
 * - 기간이 13개(extend 24개)를 넘으면 그래프 영역만 좌우로 끌어 옮기고(세로축은 고정), 처음에는 맨 끝(최근)을 보인다.
 * - overlay=empty · nan: 격자선만 그린 그래프 위에 덮개 + 안내 문구.
 */
export function TimeSeriesChart({
  type,
  series,
  overlay = 'none',
  data,
  unit,
  extend = false,
  formatValue,
  formatTick,
  formatTooltipValue,
  breaks,
  tooltipNote,
  className,
}: TimeSeriesChartProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const plotRef = useRef<SVGSVGElement>(null);
  const dragRef = useRef<{ x: number; scroll: number; moved: boolean } | null>(null);
  const gradientId = `ts-area-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const width = useElementWidth(rootRef, CHART_DEFAULT_WIDTH);
  const { tooltip, show, hide, hideNow, cancelHide, bubbleProps } = useChartTooltip();

  const message = overlay === 'none' ? null : CHART_TEXT.overlay[overlay];
  const model = useMemo(
    () => timeSeriesModel(message === null ? data : [], series, breaks),
    [message, data, series, breaks],
  );
  const valueText = formatValue ?? defaultFormat;
  const tickText = formatTick ?? valueText;
  const tooltipText = formatTooltipValue ?? valueText;
  const { labels, values, axisOf, breakBefore } = model;
  const count = labels.length;

  // ── 크기 ──
  const tickWidth = (scale: LinearScale | null) =>
    scale ? Math.max(...scale.ticks.map((tick) => estimateTextWidth(tickText(tick)))) : 0;
  const unitText = unit ? CHART_TEXT.unit(unit) : '';
  const leftWidth =
    (unitText && model.left ? L.unitWidth : 0) +
    (model.left ? tickWidth(model.left) + L.tickGap : 0);
  const rightWidth = model.right ? tickWidth(model.right) + L.tickGap : 0;
  const viewport = Math.max(0, width - leftWidth - rightWidth);
  const visible = extend ? L.visiblePeriodsExtend : L.visiblePeriods;
  const pannable = count > visible;
  const column = count === 0 ? viewport : viewport / (pannable ? visible : count);
  const plotWidth = pannable ? column * count : viewport;

  const yLeft = scaleY(model.left ?? EMPTY_SCALE, PLOT_TOP, L.plotHeight);
  const yRight = model.right ? scaleY(model.right, PLOT_TOP, L.plotHeight) : yLeft;
  const yOf = (seriesIndex: number) => (axisOf[seriesIndex] === 'right' ? yRight : yLeft);
  const baseOf = (seriesIndex: number) => {
    const scale = (axisOf[seriesIndex] === 'right' ? model.right : model.left) ?? EMPTY_SCALE;
    return yOf(seriesIndex)(Math.min(Math.max(0, scale.min), scale.max));
  };
  const xOf = (index: number) => column * (index + 0.5);
  const stride = labelStride(
    column,
    Math.max(0, ...labels.map((label) => estimateTextWidth(label))) + L.xLabelGap,
  );

  /** 가로축 글자: 양 끝에서 잘리지 않게 그래프 영역 안으로 붙인다 */
  const xLabelPosition = (index: number, label: string) => {
    const x = xOf(index);
    const half = estimateTextWidth(label) / 2;
    if (x - half < 0) return { x: 0, textAnchor: 'start' as const };
    if (x + half > plotWidth) return { x: plotWidth, textAnchor: 'end' as const };
    return { x, textAnchor: 'middle' as const };
  };

  // ── 올림 · 키보드 ──
  const activeIndex =
    tooltip?.key.startsWith(PERIOD_KEY) === true
      ? Number(tooltip.key.slice(PERIOD_KEY.length))
      : null;

  const activate = useCallback(
    (index: number) => {
      const rect = plotRef.current?.getBoundingClientRect();
      if (!rect) return;
      show({
        key: `${PERIOD_KEY}${index}`,
        content: {
          title: labels[index] ?? '',
          summary: tooltipNote,
          rows: values.map((row, seriesIndex) => {
            const value = row[index];
            return {
              color: { kind: 'series', step: seriesOf(seriesIndex) },
              name: data[seriesIndex]?.label ?? '',
              value: value === null || value === undefined ? '-' : tooltipText(value),
            };
          }),
        },
        anchor: {
          x: rect.left + column * (index + 0.5),
          y: rect.top + PLOT_TOP + L.plotHeight / 2,
        },
      });
    },
    [show, labels, values, data, tooltipText, tooltipNote, column],
  );

  const onPlotPointerMove = (event: PointerEvent<SVGSVGElement>) => {
    if (message !== null || count === 0 || dragRef.current?.moved) return;
    if ((event.target as Element).closest('[data-break]')) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const index = Math.min(
      count - 1,
      Math.max(0, Math.floor((event.clientX - rect.left) / column)),
    );
    if (index !== activeIndex) activate(index);
    else cancelHide();
  };

  const showBreak = (beforeIndex: number, text: string) => {
    const rect = plotRef.current?.getBoundingClientRect();
    if (!rect) return;
    show({
      key: `break-${beforeIndex}`,
      content: { title: text, rows: [] },
      anchor: { x: rect.left + column * beforeIndex, y: rect.top + PLOT_TOP + L.plotHeight / 2 },
    });
  };

  const scrollIntoView = (index: number) => {
    const element = scrollRef.current;
    if (!element || !pannable) return;
    const start = column * index;
    if (start < element.scrollLeft) element.scrollLeft = start;
    else if (start + column > element.scrollLeft + viewport) {
      element.scrollLeft = start + column - viewport;
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (message !== null || count === 0) return;
    const keys: Record<string, number> = {
      ArrowLeft: (activeIndex ?? count) - 1,
      ArrowRight: activeIndex === null ? count - 1 : activeIndex + 1,
      Home: 0,
      End: count - 1,
    };
    if (event.key === 'Escape') {
      hideNow();
      return;
    }
    const next = keys[event.key];
    if (next === undefined) return;
    event.preventDefault();
    const index = Math.min(count - 1, Math.max(0, next));
    scrollIntoView(index);
    activate(index);
  };

  // ── 좌우 이동(끌기) ──
  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (element && pannable) element.scrollLeft = element.scrollWidth;
  }, [pannable, count, plotWidth]);

  const onDragStart = (event: PointerEvent<HTMLDivElement>) => {
    if (!pannable || event.pointerType !== 'mouse' || event.button !== 0) return;
    dragRef.current = { x: event.clientX, scroll: event.currentTarget.scrollLeft, moved: false };
  };
  const onDragMove = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = event.clientX - drag.x;
    if (!drag.moved && Math.abs(dx) > 3) {
      drag.moved = true;
      event.currentTarget.setPointerCapture?.(event.pointerId);
      hideNow();
    }
    if (drag.moved) event.currentTarget.scrollLeft = drag.scroll - dx;
  };
  const onDragEnd = (event: PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.moved) event.currentTarget.releasePointerCapture?.(event.pointerId);
    dragRef.current = null;
  };
  const onScroll = () => {
    if (activeIndex !== null) activate(activeIndex);
    else if (tooltip) hideNow();
  };

  // ── 그림 ──
  const grid = (model.left ?? EMPTY_SCALE).ticks;
  const zeroY = crisp(yLeft(Math.min(Math.max(0, grid[0] ?? 0), grid[grid.length - 1] ?? 0)));
  const single = values.length === 1;

  const ariaLabel =
    message ??
    (count > 0
      ? CHART_TEXT.summary.timeSeries(
          data.slice(0, values.length).map((item) => item.label),
          labels[0] ?? '',
          labels[count - 1] ?? '',
          count,
        )
      : CHART_TEXT.summary.timeSeriesEmpty);

  return (
    <div
      ref={rootRef}
      data-chart="time-series"
      data-type={type}
      data-series={series}
      data-overlay={overlay}
      data-pannable={pannable || undefined}
      className={cn('relative w-full', CHART_SIZE_CLASS.timeSeries, className)}
    >
      <div
        role="img"
        aria-label={ariaLabel}
        tabIndex={message === null && count > 0 ? 0 : undefined}
        onKeyDown={onKeyDown}
        onBlur={hideNow}
        className="flex h-full w-full rounded-sm outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus"
      >
        {leftWidth > 0 ? (
          <svg aria-hidden width={leftWidth} height={HEIGHT} className="block shrink-0">
            {unitText ? (
              <text
                data-slot="unit"
                transform={`translate(${L.unitWidth / 2}, ${PLOT_TOP}) rotate(-90)`}
                textAnchor="end"
                dominantBaseline="central"
                className="fill-dataviz-axis typo-micro"
              >
                {unitText}
              </text>
            ) : null}
            {model.left ? (
              <AxisTicks scale={model.left} side="left" width={leftWidth} format={tickText} />
            ) : null}
          </svg>
        ) : null}

        <div
          ref={scrollRef}
          data-slot="scroll"
          onScroll={onScroll}
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
          className={cn(
            'scrollbar-none h-full min-w-0 flex-1',
            pannable
              ? 'cursor-grab touch-pan-x overflow-x-auto active:cursor-grabbing'
              : 'overflow-hidden',
          )}
        >
          <svg
            ref={plotRef}
            aria-hidden
            data-slot="plot"
            width={plotWidth}
            height={HEIGHT}
            onPointerMove={onPlotPointerMove}
            onPointerLeave={hide}
            className="block select-none"
          >
            <defs>
              {type === 'line'
                ? values.map((_, seriesIndex) => (
                    <linearGradient
                      key={seriesIndex}
                      id={`${gradientId}-${seriesIndex}`}
                      gradientUnits="userSpaceOnUse"
                      x1={0}
                      y1={PLOT_TOP}
                      x2={0}
                      y2={PLOT_BOTTOM}
                    >
                      <stop
                        offset={0}
                        style={{ stopColor: SERIES_COLOR_VAR[seriesOf(seriesIndex)] }}
                      />
                      <stop
                        offset={1}
                        style={{
                          stopColor: SERIES_COLOR_VAR[seriesOf(seriesIndex)],
                          stopOpacity: 0,
                        }}
                      />
                    </linearGradient>
                  ))
                : null}
            </defs>

            {/* 올린 기간 */}
            {activeIndex !== null ? (
              <rect
                data-slot="active"
                x={column * activeIndex}
                y={PLOT_TOP}
                width={column}
                height={L.plotHeight}
                className="fill-dataviz-grid opacity-muted"
              />
            ) : null}

            {/* 가로 격자선 · 바닥선(0) */}
            <g shapeRendering="crispEdges">
              {grid.map((tick) => {
                const y = crisp(yLeft(tick));
                return y === zeroY ? null : (
                  <line
                    key={tick}
                    data-slot="grid"
                    x1={0}
                    x2={plotWidth}
                    y1={y}
                    y2={y}
                    strokeWidth={1}
                    className="stroke-dataviz-grid"
                  />
                );
              })}
              <line
                data-slot="baseline"
                x1={0}
                x2={plotWidth}
                y1={zeroY}
                y2={zeroY}
                strokeWidth={1}
                className="stroke-dataviz-baseline"
              />
            </g>

            {type === 'line' ? (
              // 면 → 선 → 점 순서로 겹친다(둘째 계열의 면이 첫째 계열의 선을 가리지 않게)
              <>
                {(['area', 'line', 'dot'] as const).map((layer) => (
                  <g key={layer} data-layer={layer}>
                    {values.map((row, seriesIndex) => {
                      const y = yOf(seriesIndex);
                      const step = seriesOf(seriesIndex);
                      if (layer === 'dot') {
                        return row.map((value, index) =>
                          value === null ? null : (
                            <circle
                              key={`${seriesIndex}-${index}`}
                              data-slot="dot"
                              data-series-index={seriesIndex}
                              cx={xOf(index)}
                              cy={y(value)}
                              r={index === activeIndex ? L.activeDotRadius : L.dotRadius}
                              className={SERIES_FILL_CLASS[step]}
                            />
                          ),
                        );
                      }
                      const base = baseOf(seriesIndex);
                      return lineSegments(row, breakBefore)
                        .filter((segment) => segment.length > 1)
                        .map((segment) => {
                          const points = segment.map(
                            (index) => `${xOf(index).toFixed(2)},${y(row[index]!).toFixed(2)}`,
                          );
                          const first = xOf(segment[0]!).toFixed(2);
                          const last = xOf(segment[segment.length - 1]!).toFixed(2);
                          return layer === 'area' ? (
                            <path
                              key={`${seriesIndex}-${segment[0]}`}
                              data-slot="area"
                              data-series-index={seriesIndex}
                              d={`M${first},${base}L${points.join('L')}L${last},${base}Z`}
                              fill={`url(#${gradientId}-${seriesIndex})`}
                              className="opacity-muted"
                            />
                          ) : (
                            <path
                              key={`${seriesIndex}-${segment[0]}`}
                              data-slot="line"
                              data-series-index={seriesIndex}
                              d={`M${points.join('L')}`}
                              fill="none"
                              strokeWidth={1}
                              strokeLinejoin="round"
                              className={SERIES_STROKE_CLASS[step]}
                            />
                          );
                        });
                    })}
                  </g>
                ))}
              </>
            ) : null}
            {type === 'bar'
              ? labels.map((_, index) => (
                  <g key={index} shapeRendering="crispEdges">
                    {barDrawOrder(values, index).map((seriesIndex) => {
                      const value = values[seriesIndex]![index]!;
                      const y = yOf(seriesIndex)(value);
                      const base = baseOf(seriesIndex);
                      return (
                        <rect
                          key={seriesIndex}
                          data-slot="bar"
                          data-series-index={seriesIndex}
                          x={Math.round(xOf(index) - L.barWidth / 2)}
                          y={Math.min(y, base)}
                          width={L.barWidth}
                          height={Math.abs(base - y)}
                          className={SERIES_FILL_CLASS[seriesOf(seriesIndex)]}
                        />
                      );
                    })}
                  </g>
                ))
              : null}

            {/* 막대 위 값 글자 — 계열이 하나일 때만 */}
            {type === 'bar' && single
              ? values[0]!.map((value, index) =>
                  value === null ? null : (
                    <text
                      key={index}
                      data-slot="value"
                      x={xOf(index)}
                      y={value >= 0 ? yLeft(value) - L.valueGap : yLeft(value) + L.valueGap}
                      textAnchor="middle"
                      dominantBaseline={value >= 0 ? 'auto' : 'hanging'}
                      className="fill-dataviz-value typo-caption"
                    >
                      {valueText(value)}
                    </text>
                  ),
                )
              : null}

            {/* 가로축 글자 — 좁으면 최근 기간부터 건너뛰며 적는다 */}
            {labels.map((label, index) =>
              (count - 1 - index) % stride === 0 ? (
                <text
                  key={index}
                  data-slot="x-label"
                  {...xLabelPosition(index, label)}
                  y={PLOT_BOTTOM + L.xAxisHeight / 2}
                  dominantBaseline="central"
                  className="fill-dataviz-axis typo-caption"
                >
                  {label}
                </text>
              ) : null,
            )}

            {/* 끊김 구분선 + 올림을 받는 투명 영역 */}
            {[...breakBefore].map((beforeIndex) => {
              const x = crisp(column * beforeIndex);
              const text = breaks?.find((item) => item.beforeIndex === beforeIndex)?.tooltip ?? '';
              return (
                <g
                  key={beforeIndex}
                  data-break={beforeIndex}
                  onPointerEnter={() => showBreak(beforeIndex, text)}
                  onPointerLeave={hide}
                >
                  <line
                    data-slot="divider"
                    x1={x}
                    x2={x}
                    y1={PLOT_TOP}
                    y2={PLOT_BOTTOM}
                    strokeWidth={1}
                    shapeRendering="crispEdges"
                    className="stroke-dataviz-divider"
                  />
                  <rect
                    data-slot="divider-hit"
                    x={x - L.breakHitWidth / 2}
                    y={PLOT_TOP}
                    width={L.breakHitWidth}
                    height={L.plotHeight}
                    fill="transparent"
                  />
                </g>
              );
            })}
          </svg>
        </div>

        {model.right ? (
          <svg aria-hidden width={rightWidth} height={HEIGHT} className="block shrink-0">
            <AxisTicks scale={model.right} side="right" width={rightWidth} format={tickText} />
          </svg>
        ) : null}
      </div>

      {message !== null ? (
        <div
          aria-hidden
          data-slot="overlay"
          className="absolute inset-0 flex items-center justify-center rounded-xl bg-bg-overlay"
        >
          <p className="rounded-sm bg-dataviz-tooltip px-3 py-2 typo-body-sm-strong text-dataviz-on-tooltip shadow-popover">
            {message}
          </p>
        </div>
      ) : null}

      {tooltip ? (
        <ChartTooltip
          {...tooltip.content}
          anchor={tooltip.anchor}
          placement={tooltip.placement}
          {...bubbleProps}
        />
      ) : null}
    </div>
  );
}
